"use strict";
import { useQuery } from "@tanstack/react-query";
import { DeltaArrow } from "components/Tokens/TokenDetails/Delta";
import { LoadingBubble } from "components/Tokens/loading";
import { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { use24hRingProtocolVolume } from "state/explore/protocolStats";
import { useRingTopTokens } from "state/explore/topTokens";
import { useDefaultRingActiveTokens } from "state/lists/hooks";
import { Flex, Text, styled } from "ui/src";
import { TokenLogo } from "uniswap/src/components/CurrencyLogo/TokenLogo";
import { getNativeAddress } from "uniswap/src/constants/addresses";
import { AssetType } from "uniswap/src/entities/assets";
import { UniverseChainId } from "uniswap/src/features/chains/types";
import { fromGraphQLChain } from "uniswap/src/features/chains/utils";
import { useSwapFormContext } from "uniswap/src/features/transactions/swap/contexts/SwapFormContext";
import { NumberType, useFormatter } from "utils/formatNumbers";
const OVERVIEW_CACHE_KEY = "ring-mainnet-overview-cache-v1";
const CACHE_TTL_MS = 30 * 60 * 1e3;
function loadCachedOverview() {
  try {
    const raw = localStorage.getItem(OVERVIEW_CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return { ...parsed, isStale: Date.now() - parsed.timestamp > CACHE_TTL_MS };
  } catch {
    return null;
  }
}
function saveCachedOverview(data) {
  try {
    localStorage.setItem(OVERVIEW_CACHE_KEY, JSON.stringify({ ...data, timestamp: Date.now() }));
  } catch {
  }
}
const CardContainer = styled(Flex, {
  backgroundColor: "$surface1",
  borderRadius: "$rounded20",
  borderWidth: "$spacing1",
  borderColor: "$surface3",
  p: "$spacing12",
  gap: "$spacing12",
  width: "100%",
  position: "relative",
  overflow: "hidden",
  $md: {
    p: "$spacing10",
    borderRadius: "$rounded16"
  }
});
const MetricCard = styled(Flex, {
  backgroundColor: "$statusSuccess2",
  borderRadius: "$rounded16",
  p: "$spacing12",
  flex: 1,
  minWidth: 120,
  gap: "$spacing4",
  borderWidth: "$spacing1",
  borderColor: "$surface3",
  hoverStyle: {
    borderColor: "$surface3Hovered",
    backgroundColor: "$statusSuccess2Hovered"
  }
});
const TokenRow = styled(Flex, {
  row: true,
  alignItems: "center",
  justifyContent: "space-between",
  py: "$spacing8",
  px: "$spacing8",
  borderRadius: "$rounded12",
  cursor: "pointer",
  hoverStyle: {
    backgroundColor: "$surface2"
  },
  pressStyle: {
    backgroundColor: "$surface3"
  }
});
const ScrollableTokenList = styled(Flex, {
  gap: "$spacing4",
  backgroundColor: "$surface2",
  borderRadius: "$rounded16",
  p: "$spacing8",
  maxHeight: 360,
  className: "ring-token-scroll"
});
const SCROLLBAR_STYLE = `
  .ring-token-scroll {
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: var(--color-surface3) transparent;
  }
  .ring-token-scroll::-webkit-scrollbar {
    width: 6px;
  }
  .ring-token-scroll::-webkit-scrollbar-track {
    background: transparent;
  }
  .ring-token-scroll::-webkit-scrollbar-thumb {
    background-color: var(--color-surface3);
    border-radius: 9999px;
  }
  .ring-token-scroll::-webkit-scrollbar-thumb:hover {
    background-color: var(--color-surface3Hovered);
  }
`;
const HOT_STOCK_API_URL = `${process.env.REACT_APP_UNISWAP_BASE_API_URL ?? ""}/v2/tokenlist/stock/hot`;
const ONE_HOUR_MS = 60 * 60 * 1e3;
async function fetchHotStockTokens() {
  const response = await fetch(HOT_STOCK_API_URL, { headers: { Accept: "application/json" } });
  if (!response.ok) {
    throw new Error(`Unable to load hot stock tokens (${response.status})`);
  }
  const payload = await response.json();
  if (!Array.isArray(payload.tokens)) {
    throw new Error("Invalid hot stock token response");
  }
  return payload;
}
export const MainnetOverview = memo(function MainnetOverview2({ chainId }) {
  const { formatFiatPrice, formatDelta } = useFormatter();
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const isZh = i18n.language?.startsWith("zh");
  const isStockPage = pathname === "/stock";
  const swapFormContext = useSwapFormContext();
  const {
    totalVolume,
    totalVolumeChangePercent,
    totalTVL,
    totalTVLChangePercent,
    isLoading: isStatsLoading
  } = use24hRingProtocolVolume();
  const { topTokens, ethPrice, isLoading: isRingTokensLoading } = useRingTopTokens({ withSparklines: false });
  const ringTokens = useDefaultRingActiveTokens(chainId ?? UniverseChainId.Mainnet);
  const cached = useMemo(() => loadCachedOverview(), []);
  const [cachedDisplayTokens, setCachedDisplayTokens] = useState(cached?.displayTokens ?? null);
  const logoUrlMap = useMemo(() => {
    const map = {};
    Object.values(ringTokens).forEach((item) => {
      if (item?.address) {
        map[item.address.toLowerCase()] = item.logoURI;
      }
    });
    return map;
  }, [ringTokens]);
  const {
    data: hotStockData,
    isLoading: isTokensLoading,
    isError
  } = useQuery({
    queryKey: ["hot-stock-tokens"],
    queryFn: fetchHotStockTokens,
    staleTime: ONE_HOUR_MS,
    refetchInterval: ONE_HOUR_MS,
    refetchOnWindowFocus: false,
    enabled: isStockPage
  });
  const formatPrice = (price) => {
    if (price === void 0 || isNaN(price)) {
      return "$0.00";
    }
    return formatFiatPrice({ price, type: NumberType.ChartFiatValue });
  };
  const formatTokenPrice = (price) => {
    if (price === void 0 || price === null || isNaN(price)) {
      return "$0.00";
    }
    return formatFiatPrice({ price, type: NumberType.FiatTokenPrice });
  };
  const displayTokens = useMemo(() => {
    if (isStockPage) {
      return (hotStockData?.tokens ?? []).slice(0, 6).map((token) => ({
        key: `${token.chainId}:${token.address}`,
        address: token.address,
        chainId: token.chainId,
        symbol: token.stockSymbol || token.symbol,
        name: token.name,
        logoUrl: token.logoUrl || void 0,
        volumeUSD: token.stockVolume24hUsd,
        priceUSD: token.priceUsd
      }));
    }
    return (topTokens ?? []).slice(0, 6).map((token) => {
      const originToken = token.originToken;
      const address = originToken?.address ?? token.address;
      return {
        key: token.address,
        address,
        chainId: fromGraphQLChain(originToken?.chain ?? token.chain) ?? UniverseChainId.Mainnet,
        symbol: originToken?.symbol || token.symbol,
        name: originToken?.name || token.name,
        logoUrl: originToken?.address ? logoUrlMap[originToken.address.toLowerCase()] : void 0,
        volumeUSD: Number(token.volume) || 0,
        priceUSD: (Number(token.derivedETH) || 0) * (ethPrice || 0),
        dayChange: Number(token.pricePercentChange1Day) || 0
      };
    });
  }, [ethPrice, hotStockData?.tokens, isStockPage, logoUrlMap, topTokens]);
  const isDisplayTokensLoading = isStockPage ? isTokensLoading : isRingTokensLoading;
  const useCache = !isStockPage && (isStatsLoading || isRingTokensLoading) && cached;
  const useCachedTokens = !isStockPage && cachedDisplayTokens && displayTokens.length === 0 && isDisplayTokensLoading;
  const effectiveDisplayTokens = useCachedTokens ? cachedDisplayTokens : displayTokens;
  const effectiveIsStatsLoading = useCache ? false : isStatsLoading;
  const effectiveIsDisplayTokensLoading = useCachedTokens ? false : isDisplayTokensLoading;
  const effectiveTotalVolume = useCache ? cached.totalVolume : totalVolume;
  const effectiveTotalVolumeChangePercent = useCache ? cached.totalVolumeChangePercent : totalVolumeChangePercent;
  const effectiveTotalTVL = useCache ? cached.totalTVL : totalTVL;
  const effectiveTotalTVLChangePercent = useCache ? cached.totalTVLChangePercent : totalTVLChangePercent;
  useEffect(() => {
    if (isStockPage) {
      return;
    }
    if (displayTokens.length > 0 && !isStatsLoading && !isRingTokensLoading) {
      saveCachedOverview({
        totalVolume: totalVolume || 0,
        totalVolumeChangePercent: totalVolumeChangePercent || 0,
        totalTVL: totalTVL || 0,
        totalTVLChangePercent: totalTVLChangePercent || 0,
        displayTokens
      });
      setCachedDisplayTokens(null);
    }
  }, [
    displayTokens,
    isStatsLoading,
    isRingTokensLoading,
    isStockPage,
    totalVolume,
    totalVolumeChangePercent,
    totalTVL,
    totalTVLChangePercent
  ]);
  const handleSelectToken = (token) => {
    if (!token?.address || !swapFormContext?.updateSwapForm) {
      return;
    }
    const nextSwapForm = {
      output: {
        address: token.address,
        chainId: token.chainId,
        type: AssetType.Currency
      },
      ...swapFormContext.input?.chainId !== token.chainId ? {
        input: {
          address: getNativeAddress(token.chainId),
          chainId: token.chainId,
          type: AssetType.Currency
        }
      } : {}
    };
    swapFormContext.updateSwapForm(nextSwapForm);
  };
  return <CardContainer><style>{SCROLLBAR_STYLE}</style>{
    /* Mainnet Metric Cards */
  }<Flex row gap="$spacing8" flexWrap="wrap" width="100%"><MetricCard><Text variant="body4" color="$neutral2">{isZh ? "24h Volume (\u4EA4\u6613\u91CF)" : "24h Volume"}</Text>{effectiveIsStatsLoading ? <LoadingBubble height="22px" width="80px" /> : <Text variant="subheading1" color="$accent1">{effectiveTotalVolume ? formatPrice(effectiveTotalVolume) : "$\u2014"}</Text>}<Flex row alignItems="center" gap="$spacing2"><DeltaArrow
    delta={effectiveTotalVolumeChangePercent ?? 0}
    formattedDelta={formatDelta(effectiveTotalVolumeChangePercent)}
    size={12}
  /><Text variant="body4" color="$neutral2">{formatDelta(effectiveTotalVolumeChangePercent)}</Text></Flex></MetricCard><MetricCard><Text variant="body4" color="$neutral2">{isZh ? "Total TVL (\u603B\u9501\u4ED3\u91CF)" : "Total TVL"}</Text>{effectiveIsStatsLoading ? <LoadingBubble height="22px" width="80px" /> : <Text variant="subheading1" color="$accent1">{effectiveTotalTVL ? formatPrice(effectiveTotalTVL) : "$\u2014"}</Text>}<Flex row alignItems="center" gap="$spacing2"><DeltaArrow
    delta={effectiveTotalTVLChangePercent ?? 0}
    formattedDelta={formatDelta(effectiveTotalTVLChangePercent)}
    size={12}
  /><Text variant="body4" color="$neutral2">{formatDelta(effectiveTotalTVLChangePercent)}</Text></Flex></MetricCard></Flex>{
    /* Top Tokens Leaderboard */
  }<Flex gap="$spacing8" pt="$spacing4"><Flex row alignItems="center" justifyContent="space-between" px="$spacing4"><Text variant="subheading2" color="$neutral1">{isStockPage ? isZh ? "\u70ED\u95E8\u80A1\u7968\u4EE3\u5E01" : "Trending Stock Tokens" : "Mainnet Top Assets"}</Text><Text variant="body4" color="$neutral3">{isZh ? "\u70B9\u51FB\u4EE3\u5E01\u5FEB\u901F\u586B\u5165" : "Click to select token"}</Text></Flex><ScrollableTokenList>{effectiveIsDisplayTokensLoading && effectiveDisplayTokens.length === 0 ? <Flex gap="$spacing8" py="$spacing8" px="$spacing8"><LoadingBubble height="36px" width="100%" /><LoadingBubble height="36px" width="100%" /><LoadingBubble height="36px" width="100%" /></Flex> : isStockPage && isError && effectiveDisplayTokens.length === 0 ? <Flex alignItems="center" py="$spacing24" px="$spacing12"><Text variant="body3" color="$neutral3" textAlign="center">{isZh ? "\u70ED\u95E8\u80A1\u7968\u4EE3\u5E01\u6682\u65F6\u4E0D\u53EF\u7528" : "Trending stock tokens are temporarily unavailable"}</Text></Flex> : effectiveDisplayTokens.length === 0 ? <Flex alignItems="center" py="$spacing24" px="$spacing12"><Text variant="body3" color="$neutral3">{isStockPage ? isZh ? "\u6682\u65E0\u70ED\u95E8\u80A1\u7968\u4EE3\u5E01" : "No trending stock tokens" : isZh ? "\u6682\u65E0\u4EE3\u5E01\u6570\u636E" : "No tokens available"}</Text></Flex> : effectiveDisplayTokens.map((token, index) => {
    const dayChange = token.dayChange ?? 0;
    return <TokenRow key={token.key || index} onPress={() => handleSelectToken(token)}><Flex row alignItems="center" gap="$spacing16" flex={1} minWidth={0}><TokenLogo
      url={token.logoUrl || void 0}
      symbol={token.symbol ?? void 0}
      name={token.name ?? void 0}
      chainId={token.chainId}
      size={28}
    /><Flex minWidth={0}><Text variant="body2" color="$neutral1" fontWeight="600" numberOfLines={1}>{token.symbol}</Text><Text variant="body4" color="$neutral3" numberOfLines={1}>{token.name}</Text></Flex></Flex><Flex alignItems="flex-end" gap="$spacing2"><Text variant="body2" color="$neutral1" fontWeight="600">{isStockPage ? formatTokenPrice(token.priceUSD) : token.volumeUSD > 0 ? formatPrice(token.volumeUSD) : "$\u2014"}</Text><Flex row alignItems="center" gap="$spacing6">{!isStockPage && <><DeltaArrow delta={dayChange} formattedDelta={formatDelta(dayChange)} size={10} /><Text
      variant="body4"
      color={dayChange >= 0 ? "$statusSuccess" : "$statusCritical"}
      fontWeight="500"
    >{formatDelta(dayChange)}</Text></>}{!isStockPage && token.priceUSD !== null && token.priceUSD !== void 0 && token.priceUSD > 0 && <Text variant="body4" color="$neutral3">{formatTokenPrice(token.priceUSD)}</Text>}</Flex></Flex></TokenRow>;
  })}</ScrollableTokenList></Flex></CardContainer>;
});
